import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "./lib/realEstateI18n.tsx";
import { AuthProvider, RequireAdmin } from "./lib/auth.tsx";
import { RealEstateLayout } from "./components/RealEstateLayout.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { PageFallback } from "./components/skeletons.tsx";
import Index from "./pages/Index.tsx";
import RequestApartment from "./pages/RequestApartment.tsx";
import AvailableListings from "./pages/AvailableListings.tsx";
import ListingDetails from "./pages/ListingDetails.tsx";
import SubmitProperty from "./pages/SubmitProperty.tsx";
import WantedProperties from "./pages/WantedProperties.tsx";
import Contact from "./pages/Contact.tsx";
import Citizenship from "./pages/Citizenship.tsx";
import Services from "./pages/Services.tsx";
import ServiceDetail from "./pages/ServiceDetail.tsx";
import Auth from "./pages/Auth.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import NotFound from "./pages/NotFound.tsx";
import { Privacy, Terms, About } from "./pages/Legal.tsx";
import { InstallPrompt } from "./components/InstallPrompt.tsx";
import { ScrollToTop } from "./components/ScrollToTop.tsx";

// Lazy-loaded admin (kept out of guest bundle)
const AdminListings = lazy(() => import("./pages/AdminListings.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // listings/default: 5 min
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <ErrorBoundary>
              <Routes>
                <Route element={<RealEstateLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/request" element={<RequestApartment />} />
                  <Route path="/listings" element={<AvailableListings />} />
                  <Route path="/listings/:id" element={<ListingDetails />} />
                  <Route path="/wanted" element={<WantedProperties />} />
                  <Route path="/submit" element={<SubmitProperty />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:slug" element={<ServiceDetail />} />
                  <Route path="/citizenship" element={<Citizenship />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route
                    path="/admin/*"
                    element={
                      <RequireAdmin>
                        <Suspense fallback={<PageFallback />}>
                          <AdminListings />
                        </Suspense>
                      </RequireAdmin>
                    }
                  />
                </Route>
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <InstallPrompt />
              <ScrollToTop />
            </ErrorBoundary>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
